import { jsPDF } from "jspdf";
import { PATStatusExportRow } from "../types";
import { toDateFormat } from "@/lib/dateHelpers";

function getStatusColor(status: string): [number, number, number] {
  const upperStatus = status.toUpperCase();
  if (upperStatus === "PASSED") {
    return [40, 167, 69];
  } else if (upperStatus === "FAILED") {
    return [220, 53, 69];
  } else if (upperStatus === "QUALIFYING" || upperStatus === "QUALIFIED") {
    return [255, 193, 7];
  } else if (upperStatus === "SUSPENDED") {
    return [220, 53, 69];
  }
  return [100, 100, 100];
}

function getStatusSortOrder(status: string): number {
  const upperStatus = status.toUpperCase();
  if (upperStatus === "NOT STARTED" || !status) {
    return 0;
  } else if (upperStatus === "FAILED") {
    return 1;
  } else if (upperStatus === "QUALIFYING" || upperStatus === "QUALIFIED") {
    return 2;
  } else if (upperStatus === "PASSED") {
    return 3;
  } else if (upperStatus === "SUSPENDED") {
    return 4;
  }
  return 5;
}

export function generateAllStudentsPATStatusPDF(students: PATStatusExportRow[], universityName?: string): void {
  try {
    const sortedStudents = [...students].sort((a, b) => {
      const statusA = a.activeSuspension
        ? "SUSPENDED"
        : (a.patStatus?.toUpperCase() || "NOT STARTED").replace("_", " ");
      const statusB = b.activeSuspension
        ? "SUSPENDED"
        : (b.patStatus?.toUpperCase() || "NOT STARTED").replace("_", " ");
      return getStatusSortOrder(statusA) - getStatusSortOrder(statusB);
    });

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "letter"
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPosition = margin;
    const headerRowHeight = 10;
    const dataRowHeight = 10;

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    const title = universityName ? `PAT Status Report - ${universityName}` : "PAT Status Report";
    doc.text(title, pageWidth / 2, yPosition, { align: "center" });
    yPosition += 10;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Generated: ${toDateFormat(new Date().toISOString())} | Total Students: ${sortedStudents.length}`,
      pageWidth / 2,
      yPosition,
      { align: "center" }
    );
    doc.setTextColor(0, 0, 0);
    yPosition += 10;

    const colWidths = {
      name: 70,
      pgaId: 35,
      status: 35,
      suspended: 30,
      patDate: 40,
      patScore: 30
    };

    const startX = margin;
    let currentX = startX;

    doc.setFillColor(50, 50, 50);
    doc.rect(startX, yPosition - 5, pageWidth - 2 * margin, headerRowHeight, "F");

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);

    doc.text("Name", currentX + 3, yPosition);
    currentX += colWidths.name;

    doc.text("PGA ID", currentX + 3, yPosition);
    currentX += colWidths.pgaId;

    doc.text("Status", currentX + 3, yPosition);
    currentX += colWidths.status;

    doc.text("Suspension", currentX + colWidths.suspended / 2, yPosition, { align: "center" });
    currentX += colWidths.suspended;

    doc.text("PAT Date", currentX + 3, yPosition);
    currentX += colWidths.patDate;

    doc.text("PAT Score", currentX + colWidths.patScore / 2, yPosition, { align: "center" });

    doc.setTextColor(0, 0, 0);
    yPosition += headerRowHeight + 3;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");

    sortedStudents.forEach((student, index) => {
      if (yPosition + dataRowHeight > pageHeight - margin - 10) {
        doc.addPage();
        yPosition = margin + 15;

        doc.setFillColor(50, 50, 50);
        doc.rect(startX, yPosition - 5, pageWidth - 2 * margin, headerRowHeight, "F");
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(255, 255, 255);
        currentX = startX;
        doc.text("Name", currentX + 3, yPosition);
        currentX += colWidths.name;
        doc.text("PGA ID", currentX + 3, yPosition);
        currentX += colWidths.pgaId;
        doc.text("Status", currentX + 3, yPosition);
        currentX += colWidths.status;
        doc.text("Suspension", currentX + colWidths.suspended / 2, yPosition, { align: "center" });
        currentX += colWidths.suspended;
        doc.text("PAT Date", currentX + 3, yPosition);
        currentX += colWidths.patDate;
        doc.text("PAT Score", currentX + colWidths.patScore / 2, yPosition, { align: "center" });
        doc.setTextColor(0, 0, 0);
        yPosition += headerRowHeight + 3;
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
      }

      if (index % 2 === 0) {
        doc.setFillColor(245, 245, 245);
        doc.rect(startX, yPosition - 5, pageWidth - 2 * margin, dataRowHeight, "F");
      }

      doc.setDrawColor(200, 200, 200);
      doc.rect(startX, yPosition - 5, pageWidth - 2 * margin, dataRowHeight);

      currentX = startX;
      doc.setTextColor(0, 0, 0);

      const name = doc.splitTextToSize(student.displayName || "N/A", colWidths.name - 5);
      doc.text(name[0] || "N/A", currentX + 3, yPosition);
      currentX += colWidths.name;

      doc.text(student.pgaId || "N/A", currentX + 3, yPosition);
      currentX += colWidths.pgaId;

      const patStatus = student.activeSuspension
        ? "SUSPENDED"
        : (student.patStatus?.toUpperCase() || "NOT STARTED").replace("_", " ");
      const statusColor = getStatusColor(patStatus);
      doc.setFillColor(...statusColor);
      doc.roundedRect(currentX + 2, yPosition - 4, colWidths.status - 4, dataRowHeight - 2, 2, 2, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      const statusText = doc.splitTextToSize(patStatus, colWidths.status - 6);
      doc.text(statusText[0] || "N/A", currentX + colWidths.status / 2, yPosition, { align: "center" });
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      currentX += colWidths.status;

      if (student.activeSuspension) {
        doc.setTextColor(220, 53, 69);
        doc.text("Y", currentX + colWidths.suspended / 2, yPosition, { align: "center" });
        doc.setTextColor(0, 0, 0);
      } else {
        doc.text("--", currentX + colWidths.suspended / 2, yPosition, { align: "center" });
      }
      currentX += colWidths.suspended;

      // PAT Date column - shows date based on status (passed, failed, or qualifying)
      if (student.patDate !== null && student.patDate !== undefined) {
        const patDateFormatted = toDateFormat(student.patDate);
        doc.text(patDateFormatted || "--", currentX + 3, yPosition);
      } else {
        doc.text("--", currentX + 3, yPosition);
      }
      currentX += colWidths.patDate;

      // PAT Score column - shows score based on status (passed, failed, or qualifying round score)
      if (student.patScore !== null && student.patScore !== undefined) {
        doc.text(student.patScore.toString(), currentX + colWidths.patScore / 2, yPosition, {
          align: "center"
        });
      } else {
        doc.text("--", currentX + colWidths.patScore / 2, yPosition, { align: "center" });
      }
      currentX += colWidths.patScore;

      yPosition += dataRowHeight;
    });

    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 5, {
        align: "center"
      });
      doc.setTextColor(0, 0, 0);
    }

    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `PAT_Status_Report_${timestamp}.pdf`;
    doc.save(filename);
  } catch (error) {
    throw new Error(`Failed to generate PDF: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}
